/**
 * Demo Expired Overlay — Fullscreen-Overlay wenn die Demo abgelaufen ist.
 * Enthält E-Mail-Eingabe für Follow-Up-E-Mail + CTA zu /termin.
 */
import { useState } from "react";
import { useDemo } from "@/contexts/DemoContext";
import { Button } from "@/components/ui/button";
import { Clock, Calendar, ArrowRight, Sparkles, Mail, Check, Loader2 } from "lucide-react";

export default function DemoExpiredOverlay() {
  const { isDemo, expired } = useDemo();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  if (!isDemo || !expired) return null;

  const handleSubmitEmail = async () => {
    if (!email || !email.includes("@")) {
      setError("Bitte gib eine gueltige E-Mail-Adresse ein.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const res = await fetch("/api/demo/follow-up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), firstName: firstName.trim() || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
      } else {
        setError(data.error || "Etwas ist schiefgelaufen.");
      }
    } catch {
      setError("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="mx-4 max-w-md rounded-2xl bg-white p-8 text-center shadow-2xl">
        {/* Icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
          <Clock className="h-8 w-8 text-amber-600" />
        </div>

        {/* Title */}
        <h2 className="mb-3 text-2xl font-bold text-gray-900">
          Deine Demo-Zeit ist abgelaufen
        </h2>

        {/* Description */}
        <p className="mb-6 text-gray-600 leading-relaxed">
          Du hast einen Einblick in AYU bekommen — dein persoenlicher Gesundheitsbegleiter 
          basierend auf Ayurveda. Bereit fuer deinen eigenen Weg?
        </p>

        {/* Features preview */}
        <div className="mb-6 space-y-2 text-left">
          {[
            "Persoenlicher AYU-Coach mit KI",
            "Taeglich angepasste Routinen",
            "Fortschritts-Tracking & Kompass",
            "Community-Austausch",
          ].map((feature) => (
            <div key={feature} className="flex items-center gap-2 text-sm text-gray-700">
              <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Email Follow-Up Section */}
        {!sent ? (
          <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-medium text-gray-800">
                Erinnerung erhalten
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Wir senden dir alle Infos per E-Mail — kostenlos und unverbindlich.
            </p>
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Vorname (optional)"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-colors"
              />
              <input
                type="email"
                placeholder="Deine E-Mail-Adresse"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleSubmitEmail()}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-amber-400 focus:ring-1 focus:ring-amber-400 outline-none transition-colors"
              />
              {error && (
                <p className="text-xs text-red-500">{error}</p>
              )}
              <Button
                onClick={handleSubmitEmail}
                disabled={sending || !email}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white text-sm py-2 rounded-lg"
                size="sm"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Mail className="h-4 w-4 mr-2" />
                )}
                Infos zusenden
              </Button>
            </div>
          </div>
        ) : (
          <div className="mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
            <div className="flex items-center justify-center gap-2 text-green-700">
              <Check className="h-5 w-5" />
              <span className="text-sm font-medium">E-Mail gesendet!</span>
            </div>
            <p className="text-xs text-green-600 mt-1">
              Schau in dein Postfach — wir haben dir alle Infos geschickt.
            </p>
          </div>
        )}

        {/* Primary CTA */}
        <a href="/termin" className="block w-full">
          <Button
            className="w-full bg-amber-600 hover:bg-amber-700 text-white text-base py-6 rounded-xl"
            size="lg"
          >
            <Calendar className="mr-2 h-5 w-5" />
            Kostenlose Beratung buchen
          </Button>
        </a>

        {/* Secondary CTA */}
        <a
          href="/"
          className="mt-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Zurueck zur Startseite <ArrowRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
